import { Component, computed, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { ErrorFieldComponent } from '../error-field/error-field.component';

@Component({
  selector: 'siat-date-range-field',
  templateUrl: './date-range-field.component.html',
  styleUrl: './date-range-field.component.scss',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    TranslatePipe,
    ErrorFieldComponent,
  ],
  providers: [provideNativeDateAdapter()],
})
export class DateRangeFieldComponent {
  /**
   * The form group holding the start and end controls.
   */
  readonly formGroup = input.required<FormGroup>();

  /**
   * The name of the start date control.
   */
  readonly startControlName = input('start');

  /**
   * The name of the end date control.
   */
  readonly endControlName = input('end');

  /**
   * The field label.
   */
  readonly label = input('');

  /**
   * The start date placeholder, defaults to the translated "Data inizio".
   */
  readonly startPlaceholder = input<string>();

  /**
   * The end date placeholder, defaults to the translated "Data fine".
   */
  readonly endPlaceholder = input<string>();

  /**
   * Fills the end date with the start date when the picker closes with only the start set.
   */
  readonly fillEndWithStart = input(false);

  /**
   * The hint shown below the field.
   */
  readonly hint = input<string>();

  /**
   * The minimum selectable date.
   */
  readonly minDate = input<Date | null>(null);

  /**
   * The maximum selectable date.
   */
  readonly maxDate = input<Date | null>(null);

  /**
   * Shows the cancel/apply buttons inside the picker.
   */
  readonly withActions = input(false);

  /**
   * The appearance of the form field.
   */
  readonly appearance = input<MatFormFieldAppearance>('outline');

  /**
   * Emits when the picker is closed.
   */
  readonly closed = output<void>();

  /**
   * The start date control.
   */
  readonly startControl = computed(
    () =>
      this.formGroup().controls[
        this.startControlName()
      ] as FormControl<Date | null>,
  );

  /**
   * The end date control.
   */
  readonly endControl = computed(
    () =>
      this.formGroup().controls[
        this.endControlName()
      ] as FormControl<Date | null>,
  );

  /**
   * Completes a single-day range when enabled, then notifies the picker closing.
   */
  onClosed(): void {
    const start = this.startControl().value;
    const end = this.endControl();
    if (this.fillEndWithStart() && start && !end.value) {
      end.setValue(start);
    }
    this.closed.emit();
  }
}
